import React, { useState, useRef, useEffect } from 'react';
import { PlayIcon, VideoCameraIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import toast from 'react-hot-toast';

export default function SessionVideoPlayer({
  session,
  interviewDetails,
  candidateName,
  title,
  context = 'interview',
  // When set, fetch the recording URL through the public share-code endpoint
  // (hiring-manager review) instead of the authenticated recruiter routes.
  publicShareCode = '',
}) {
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [recordingLoading, setRecordingLoading] = useState(false);
  const videoRef = useRef(null);

  // For assessments, we often have the URL right in the session object
  let directVideoUrl = session?.cameraRecording?.videoUrl || session?.videoUrl || null;
  
  // If the URL is an S3 key (does not start with http or blob:), we must fetch a presigned URL
  const isS3Key = directVideoUrl && !directVideoUrl.startsWith('http') && !directVideoUrl.startsWith('blob:');
  if (isS3Key) {
    directVideoUrl = null;
  }
  
  // Is there any form of recording to show?
  const hasRecording = session?.hasRecording || !!directVideoUrl || isS3Key;
  
  // Recording type: fallback to video if not explicitly audio
  const recordingType = session?.recordingType || 'video';

  useEffect(() => {
    // If a direct URL exists, use it immediately
    if (directVideoUrl) {
      setRecordingUrl(directVideoUrl);
    } else {
      setRecordingUrl(null);
    }
    
    // Pause existing video if session changes
    if (videoRef.current) {
      videoRef.current.pause();
    }
  }, [session?.id, session?._id, directVideoUrl]);

  const handleFetchRecording = async () => {
    if (directVideoUrl) return; // already handled
    
    const sessionId = session?.id || session?._id;
    if (!sessionId) return;
    
    setRecordingLoading(true);
    try {
      const endpoint = publicShareCode
        ? `/api/screenings/public/candidate-review/${publicShareCode}/interview-session/${sessionId}/recording-url`
        : context === 'assessment' || session?.assessmentId
          ? `/api/proctoring/session/${sessionId}/recording-url`
          : `/api/ai-interviews/session/${sessionId}/recording-url`;
      const res = await axios.get(endpoint);
      setRecordingUrl(res.data.url);
    } catch (err) {
      if (err.response?.status === 202) {
        toast('Recording is still being processed. Try again shortly.');
      } else {
        toast.error('Could not load recording');
      }
    } finally {
      setRecordingLoading(false);
    }
  };

  const displayName = candidateName || session?.candidateName || 'Candidate';
  const displayTitle = title || interviewDetails?.title || (context === 'assessment' ? 'Assessment Session' : 'Interview Session');

  return (
    <div className="w-full h-full bg-gradient-to-br from-slate-900 via-slate-950 to-zinc-900 rounded-2xl overflow-hidden flex flex-col relative shadow-2xl border border-white/10 ring-1 ring-black/40" style={{ minHeight: '260px' }}>
      <div className="flex-1 flex items-center justify-center relative">
        {hasRecording ? (
          recordingUrl ? (
            recordingType === 'video' || directVideoUrl ? (
              <video 
                ref={videoRef} 
                src={recordingUrl} 
                controls 
                autoPlay 
                className="w-full h-full object-contain bg-black" 
                onLoadedMetadata={(e) => {
                  const video = e.target;
                  // Fix for raw WebM files showing Infinity or missing duration
                  if (video.duration === Infinity || isNaN(video.duration)) {
                    video.currentTime = 1e101; // Seek to end to force Chrome to calculate duration
                    video.ontimeupdate = () => {
                      video.ontimeupdate = null; // Remove listener
                      video.currentTime = 0; // Seek back to start
                    };
                  }
                }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-slate-800 p-8">
                <audio 
                  ref={videoRef} 
                  src={recordingUrl} 
                  controls 
                  autoPlay 
                  className="w-full" 
                  onLoadedMetadata={(e) => {
                    const media = e.target;
                    if (media.duration === Infinity || isNaN(media.duration)) {
                      media.currentTime = 1e101;
                      media.ontimeupdate = () => {
                        media.ontimeupdate = null;
                        media.currentTime = 0;
                      };
                    }
                  }}
                />
              </div>
            )
          ) : (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gradient-to-br from-slate-800 to-slate-900">
              <button
                type="button"
                onClick={handleFetchRecording}
                disabled={recordingLoading}
                className="bg-indigo-600 hover:bg-indigo-500 text-white rounded-full w-16 h-16 flex items-center justify-center shadow-xl transition-transform hover:scale-110 disabled:opacity-50"
              >
                {recordingLoading ? (
                   <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                   <PlayIcon className="w-8 h-8 ml-1" />
                )}
              </button>
              <p className="mt-4 text-sm font-medium text-slate-300">
                {recordingType === 'video' ? 'Play Video Recording' : 'Play Audio Recording'}
              </p>
            </div>
          )
        ) : (
          <div className="text-slate-500 flex flex-col items-center">
            <VideoCameraIcon className="w-12 h-12 mb-2 opacity-20" />
            <span className="text-sm font-medium">No recording available</span>
          </div>
        )}
      </div>
      
      {/* Top Left Badge */}
      <div className="absolute top-4 left-4 flex items-center gap-2 bg-white/10 backdrop-blur-md text-white text-[11px] font-semibold px-3 py-1.5 rounded-full border border-white/20 shadow-lg">
        <VideoCameraIcon className="w-4 h-4" />
        <span>
          {recordingType === 'audio' && !directVideoUrl
            ? 'Audio Replay'
            : 'Video Replay'}
        </span>
      </div>
      
      {/* Top Right Date */}
      <div className="absolute top-4 right-4 text-[11px] font-semibold text-white/80 px-3 py-1 rounded-full border border-white/20 bg-white/5 backdrop-blur-md">
        {session?.startedAt
          ? new Date(session.startedAt).toLocaleDateString()
          : 'Session'}
      </div>
      
      {/* Bottom Text Overlay (only when not playing yet) */}
      {!recordingUrl && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-slate-900 via-slate-900/80 to-transparent px-5 pt-10 pb-5 pointer-events-none">
          <h4 className="text-white font-bold text-lg">{displayName}</h4>
          <p className="text-slate-300 text-sm truncate">{displayTitle}</p>
        </div>
      )}
    </div>
  );
}
